import React, { useState, useEffect } from 'react';
import { X, ChevronRight, Flame, Trophy, HeartCrack } from 'lucide-react';
import { notificationsApi } from '../../services/api';
import { useDashboard } from './DashboardContext';
import useVisiblePolling from '../../hooks/useVisiblePolling';

const STYLE_BY_TYPE = {
  streak_protection: {
    icon: Flame,
    gradient: 'linear-gradient(135deg, #f97316, #ea580c)',
    border: 'rgba(249, 115, 22, 0.35)',
    glow: 'rgba(249, 115, 22, 0.25)',
  },
  streak_broken: {
    icon: HeartCrack,
    gradient: 'linear-gradient(135deg, #ef4444, #b91c1c)',
    border: 'rgba(239, 68, 68, 0.35)',
    glow: 'rgba(239, 68, 68, 0.2)',
  },
  milestone: {
    icon: Trophy,
    gradient: 'linear-gradient(135deg, #fbbf24, #d97706)',
    border: 'rgba(251, 191, 36, 0.35)',
    glow: 'rgba(251, 191, 36, 0.25)',
  },
};

const NudgeBanner = () => {
  const { setActivePage } = useDashboard();
  const [notifications, setNotifications] = useState([]);

  const fetchNotifications = async () => {
    try {
      const data = await notificationsApi.list();
      setNotifications(data?.notifications || []);
    } catch (err) {
      console.warn('Failed to fetch nudges:', err);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  // Re-poll every 60s so new nudges from the scheduler land without a reload.
  useVisiblePolling(fetchNotifications, { intervalMs: 60000 });

  const handleDismiss = async (id) => {
    // Optimistic remove
    setNotifications(prev => prev.filter(n => n.id !== id));
    try {
      await notificationsApi.dismiss(id);
    } catch (err) {
      console.error('Failed to dismiss notification:', err);
    }
  };

  const handleCta = (n) => {
    // Navigate inside the dashboard router. Links like "/performance" map to
    // activePage strings used by index.js.
    const link = n.cta_link || '';
    if (link.includes('performance')) {
      setActivePage('Performance');
    } else if (link.includes('coach')) {
      setActivePage('My Coach');
    } else if (link.includes('community')) {
      setActivePage('Community');
    } else if (link.includes('courses')) {
      setActivePage('Courses');
    }
  };

  if (!notifications || notifications.length === 0) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
      {notifications.map(n => {
        const style = STYLE_BY_TYPE[n.type] || STYLE_BY_TYPE.streak_protection;
        const Icon = style.icon;
        return (
          <div
            key={n.id}
            data-testid={`nudge-banner-${n.type}`}
            style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              padding: '14px 18px',
              borderRadius: 12,
              background: 'rgba(15, 23, 42, 0.75)',
              border: `1px solid ${style.border}`,
              boxShadow: `0 0 24px ${style.glow}`,
              overflow: 'hidden',
            }}
          >
            {/* Left accent bar */}
            <div style={{
              position: 'absolute', left: 0, top: 0, bottom: 0, width: 4,
              background: style.gradient,
            }} />

            {/* Icon */}
            <div style={{
              flexShrink: 0, width: 40, height: 40, borderRadius: 10,
              background: style.gradient,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff',
            }}>
              <Icon size={20} />
            </div>

            {/* Body */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: '#fff', fontSize: 15, fontWeight: 700, lineHeight: 1.3 }}>
                {n.title}
              </div>
              <div style={{ color: '#d1d5db', fontSize: 13, marginTop: 2 }}>
                {n.message}
              </div>
            </div>

            {/* CTA */}
            {n.cta_text && (
              <button
                onClick={() => handleCta(n)}
                data-testid={`nudge-cta-${n.type}`}
                style={{
                  flexShrink: 0,
                  display: 'flex', alignItems: 'center', gap: 4,
                  padding: '8px 14px',
                  borderRadius: 8,
                  border: 'none',
                  background: style.gradient,
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                {n.cta_text} <ChevronRight size={14} />
              </button>
            )}

            {/* Dismiss */}
            <button
              onClick={() => handleDismiss(n.id)}
              data-testid={`nudge-dismiss-${n.type}`}
              aria-label="Dismiss notification"
              style={{
                flexShrink: 0,
                width: 28, height: 28, borderRadius: 6,
                border: 'none',
                background: 'rgba(255,255,255,0.06)',
                color: '#9ca3af',
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <X size={16} />
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default NudgeBanner;
